import { ReactNode, useEffect } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { ArrowLeftIcon, BellIcon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { env } from '@campai/lib-web/env'
import { BrandLogo } from '@campai/lib-web/components'
import { Button, Link, type LinkNavigationProps } from '@campai/lib-web/components/new/ui'

import { appState } from '@/appState'
import { canReadCommunity } from '@/product/community/access'
import { canReadFeedback } from '@/product/feedback/access'

type PageProps = {
    title: string
    description?: ReactNode
    parentLink?: LinkNavigationProps
    className?: string
    headerClassName?: string
    descriptionClassName?: string
    children?: ReactNode
}

export const Page = observer(
    ({
        title,
        description,
        parentLink = { to: '/' },
        className,
        headerClassName,
        descriptionClassName,
        children,
    }: PageProps) => {
        const { t } = useTranslation()
        const pathname = useRouterState({ select: (state) => state.location.pathname })
        const showBackToHome = pathname !== '/'
        const unreadNotificationsCount = appState.watchCounters.notifications.counter
        const hasCommunityAccess = canReadCommunity()
        const hasFeedbackAccess = canReadFeedback()

        useEffect(() => {
            const productName = env.VITE_PRODUCT_NAME
            document.title = `${title} • ${productName}`

            return () => {
                document.title = productName
            }
        }, [title])

        return (
            <section className={className || 'space-y-6 pb-8'}>
                <div className={`space-y-5 sm:space-y-2 ${headerClassName || ''}`}>
                    <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <Link
                            variant="unstyled"
                            {...parentLink}
                            active={false}
                            className="group inline-flex items-center gap-2 rounded-xl text-neutral-950 transition-[transform,filter,opacity] duration-150 hover:opacity-90 hover:drop-shadow-[0_8px_18px_rgba(15,23,42,0.12)] active:scale-[0.975] active:opacity-80 dark:text-white dark:hover:drop-shadow-[0_10px_24px_rgba(0,0,0,0.35)]"
                        >
                            <div className="flex h-11 items-center gap-2.5 text-neutral-950 dark:text-white">
                                {showBackToHome && (
                                    <ArrowLeftIcon className="size-5 shrink-0 transition-transform duration-150 group-hover:-translate-x-0.5 group-active:-translate-x-1" />
                                )}
                                <BrandLogo className="h-5 w-auto sm:h-6" />
                            </div>
                        </Link>
                        <div className="flex min-w-0 flex-wrap items-center justify-start gap-2 sm:justify-end">
                            <Button
                                variant="outline"
                                className="bg-white/90 shadow-sm backdrop-blur dark:bg-neutral-950/80"
                                nativeButton={false}
                                render={<Link variant="unstyled" to="/" active={false} />}
                            >
                                {t('home.title')}
                            </Button>
                            {hasCommunityAccess && (
                                <Button
                                    variant="outline"
                                    className="bg-white/90 shadow-sm backdrop-blur dark:bg-neutral-950/80"
                                    nativeButton={false}
                                    render={<Link variant="unstyled" to="/product/community" active={false} />}
                                >
                                    {t('home.community.title')}
                                </Button>
                            )}
                            {hasFeedbackAccess && (
                                <Button
                                    variant="outline"
                                    className="bg-white/90 shadow-sm backdrop-blur dark:bg-neutral-950/80"
                                    nativeButton={false}
                                    render={<Link variant="unstyled" to="/product/feedback" active={false} />}
                                >
                                    {t('home.feedback.title')}
                                </Button>
                            )}
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="relative shrink-0 bg-white/90 shadow-sm backdrop-blur dark:bg-neutral-950/80"
                                onClick={() => {
                                    appState.isNotificationsVisible = true
                                }}
                                title={t('notifications.panel.title')}
                                data-title-direction="bottom"
                            >
                                <BellIcon />
                                {unreadNotificationsCount > 0 && (
                                    <span className="absolute -right-1 -top-1 min-w-4 rounded-full bg-blue-600 px-1 text-center text-[10px] font-semibold leading-4 text-white dark:bg-blue-400 dark:text-blue-950">
                                        {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                                    </span>
                                )}
                            </Button>
                        </div>
                    </div>
                    <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
                    {typeof description === 'string'
                        ? description && (
                              <p className={`text-muted-foreground text-sm ${descriptionClassName || 'max-w-3xl'}`}>
                                  {description}
                              </p>
                          )
                        : description && (
                              <div className={`text-muted-foreground text-sm ${descriptionClassName || 'max-w-3xl'}`}>
                                  {description}
                              </div>
                          )}
                </div>
                {children}
            </section>
        )
    }
)
